import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiService } from '../services/apiService';

interface StudentProfile {
  currentStep: number;
  responses: {
    academicBackground: string[];
    learningGoals: string[];
    preferredLearningStyles: string[];
    languageProficiency: string[];
    comprehensionLevels: string[];
    feedbackPreferences: string[];
    individualInterests: string[];
    participationcomfort: string[];
    learningenvironment: string[];
    communicationstyle: string[];
    workloadPreferences: string[];
    extraCurricular: string[];
    learningPace: string[];
    assessmentStyles: string[];
    pastPerformance: string[];
    feedbackUtilization: string[];
    personalBackground: string[];
  };
}

interface StudentProfileStore {
  profile: StudentProfile;
  classId: string | null;
  userId: string | null;
  loading: boolean;
  setResponse: (category: keyof StudentProfile['responses'], response: string[]) => void;
  nextStep: () => void;
  previousStep: () => void;
  setClassId: (classId: string) => void;
  setUserId: (userId: string) => void;
  saveProfile: () => Promise<void>;
  loadProfile: (userId: string, classId: string) => Promise<void>;
  reset: () => void;
}

const initialProfile: StudentProfile = {
  currentStep: 1,
  responses: {
    academicBackground: [],
    learningGoals: [],
    preferredLearningStyles: [],
    languageProficiency: [],
    comprehensionLevels: [],
    feedbackPreferences: [],
    individualInterests: [],
    participationcomfort: [],
    learningenvironment: [],
    communicationstyle: [],
    workloadPreferences: [],
    extraCurricular: [],
    learningPace: [],
    assessmentStyles: [],
    pastPerformance: [],
    feedbackUtilization: [],
    personalBackground: [],
  },
};

export const useStudentProfileStore = create<StudentProfileStore>()(
  persist(
    (set, get) => ({
      profile: initialProfile,
      classId: null,
      userId: null,
      loading: false,
      setResponse: (category, response) =>
        set((state) => ({
          profile: {
            ...state.profile,
            responses: {
              ...state.profile.responses,
              [category]: response,
            },
          },
        })),
      nextStep: () =>
        set((state) => ({
          profile: {
            ...state.profile,
            currentStep: state.profile.currentStep + 1,
          },
        })),
      previousStep: () =>
        set((state) => ({
          profile: {
            ...state.profile,
            currentStep: Math.max(1, state.profile.currentStep - 1),
          },
        })),
      setClassId: (classId) => set({ classId }),
      setUserId: (userId) => set({ userId }),
      saveProfile: async () => {
        const { userId, classId, profile } = get();
        if (!userId || !classId) return;
        
        set({ loading: true });
        try {
          await apiService.saveStudentProfile({
            user_id: userId,
            class_id: classId,
            responses: profile.responses,
            current_step: profile.currentStep,
            completed: profile.currentStep >= 17
          });
        } catch (error) {
          console.error('Failed to save profile:', error);
        } finally {
          set({ loading: false });
        }
      },
      loadProfile: async (userId: string, classId: string) => {
        set({ loading: true, userId, classId });
        try {
          const { data } = await apiService.getStudentProfile(userId, classId);
          if (data) {
            set({
              profile: {
                currentStep: data.currentStep || 1,
                responses: data.responses as StudentProfile['responses'],
              }
            });
          }
        } catch (error) {
          console.error('Failed to load profile:', error);
        } finally {
          set({ loading: false });
        }
      },
      reset: () => set({ 
        profile: initialProfile, 
        classId: null, 
        userId: null 
      }),
    }),
    {
      name: 'synced-student-profile-store',
      partialize: (state) => ({ 
        profile: state.profile,
        classId: state.classId,
        userId: state.userId 
      }),
    }
  )
);